import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import apiClient from '../../api/apiClient';
import { ArrowLeft, Edit } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { createPageUrl } from '../../utils';

const DELIVERY_METHOD_LABELS = {
  online: 'Online',
  offline: 'Offline',
  hybrid: 'Hybrid',
};

const STATUS_LABELS = {
  planned: 'Direncanakan',
  ongoing: 'Berlangsung',
  completed: 'Selesai',
  cancelled: 'Dibatalkan',
};

const STATUS_COLORS = {
  planned: 'bg-blue-100 text-blue-700',
  ongoing: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

const formatCurrency = value => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDate = dateString => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // If invalid, try to parse as date string (YYYY-MM-DD)
      const parts = dateString.split('T')[0].split('-');
      if (parts.length === 3) {
        return new Intl.DateTimeFormat('id-ID', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(new Date(parts[0], parts[1] - 1, parts[2]));
      }
      return dateString;
    }

    return new Intl.DateTimeFormat('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    // Fallback: if date string is in YYYY-MM-DD format, format it manually
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
      const parts = dateString.split('T')[0].split('-');
      const months = [
        'Januari',
        'Februari',
        'Maret',
        'April',
        'Mei',
        'Juni',
        'Juli',
        'Agustus',
        'September',
        'Oktober',
        'November',
        'Desember',
      ];
      return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    }
    return dateString;
  }
};

export default function EmployeeDevelopmentDetail() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');

  const [loading, setLoading] = useState(true);
  const [programData, setProgramData] = useState<any>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get(`/hris/development/${id}`);
      if (data) {
        setProgramData(data);
      } else {
        showError('Program pengembangan tidak ditemukan');
        navigate(-1);
      }
    } catch (error: any) {
      showError('Gagal memuat data program pengembangan');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, showError]);

  useEffect(() => {
    if (id) {
      loadData();
    } else {
      showError('ID program tidak ditemukan');
      navigate(-1);
    }
  }, [id, loadData, showError, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!programData) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Data program tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detail Program Pengembangan</h1>
          </div>
        </div>
        <button
          onClick={() => navigate(createPageUrl(`EmployeeDevelopmentForm?id=${id}`))}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:scale-95 flex items-center gap-2 transition-all duration-300"
        >
          <Edit className="w-4 h-4" />
          Edit Program
        </button>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
        <div>
          <h3 className="text-lg font-semibold">Informasi Program</h3>
          <p className="text-gray-500 text-sm">Detail program pengembangan pegawai</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Tahun</label>
            <p className="text-base text-gray-900 font-medium">{programData.year || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Nama Program</label>
            <p className="text-base text-gray-900 font-medium">{programData.program_name || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Target Waktu</label>
            <p className="text-base text-gray-900 font-medium">
              {formatDate(programData.target_date)}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Budget</label>
            <p className="text-base text-gray-900 font-medium">
              {formatCurrency(programData.budget)}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Pengajar</label>
            <p className="text-base text-gray-900 font-medium">{programData.instructor || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Penyelenggara</label>
            <p className="text-base text-gray-900 font-medium">{programData.organizer || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">
              Metode Pelaksanaan
            </label>
            <p className="text-base text-gray-900 font-medium">
              {DELIVERY_METHOD_LABELS[programData.delivery_method] ||
                programData.delivery_method ||
                '-'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
            <span
              className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                STATUS_COLORS[programData.status] || 'bg-gray-100 text-gray-700'
              }`}
            >
              {STATUS_LABELS[programData.status] || programData.status || '-'}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Tujuan</label>
          <p className="text-base text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-md border border-gray-200 min-h-[80px]">
            {programData.objective || '-'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-500 mb-1">Evaluasi</label>
          <p className="text-base text-gray-900 whitespace-pre-wrap bg-gray-50 p-3 rounded-md border border-gray-200 min-h-[80px]">
            {programData.evaluation || '-'}
          </p>
        </div>
      </div>
    </div>
  );
}
